import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { Select, type SelectOption } from "../Select";
import { Button } from "../Button";
import { ButtonGroup } from "../ButtonGroup";

const paginationVariants = cva(
  "flex items-center gap-4 font-display text-body-medium-medium",
  {
    variants: {
      size: {
        small: "text-xs gap-2",
        medium: "text-sm gap-4",
        large: "text-base gap-5",
      },
    },
    defaultVariants: {
      size: "medium",
    },
  }
);

export interface PaginationProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  /**
   * Current page number (1-indexed)
   */
  currentPage?: number;
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Rows per page value
   */
  rowsPerPage?: number;
  /**
   * Available options for rows per page
   */
  rowsPerPageOptions?: number[];
  /**
   * Show rows per page selector
   */
  showRowsPerPage?: boolean;
  /**
   * Callback when page changes
   */
  onPageChange?: (page: number) => void;
  /**
   * Callback when rows per page changes
   */
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  /**
   * Size of the pagination controls
   */
  size?: "small" | "medium" | "large";
  /**
   * Disable the pagination controls
   */
  isDisabled?: boolean;
  /**
   * Custom text for "Row per page" label
   */
  rowsPerPageLabel?: string;
  /**
   * Custom text for "of" label
   */
  ofLabel?: string;
  /**
   * Custom text for "Prev" button
   */
  prevLabel?: string;
  /**
   * Custom text for "Next" button
   */
  nextLabel?: string;
  /**
   * Show prev/next buttons
   */
  showPrevNext?: boolean;
  /**
   * Show page jumper (dropdown) instead of numbered buttons
   */
  showPageJumper?: boolean;
  /**
   * Show page number (used with showPageJumper=false)
   */
  showPageNumber?: boolean;
  /**
   * Page status for displaying page numbers
   */
  pageStatus?: "none" | "first" | "middle" | "last";
  /**
   * Maximum number of page buttons to show
   */
  maxPageButtons?: number;
}

export const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      className,
      currentPage: controlledCurrentPage,
      totalPages,
      rowsPerPage: controlledRowsPerPage,
      rowsPerPageOptions = [5, 10, 15, 20, 25, 50, 100],
      showRowsPerPage = true,
      onPageChange,
      onRowsPerPageChange,
      size = "medium",
      isDisabled = false,
      rowsPerPageLabel = "Row per page",
      ofLabel = "of",
      prevLabel = "Prev",
      nextLabel = "Next",
      showPrevNext = true,
      showPageJumper = true,
      showPageNumber = true,
      pageStatus,
      maxPageButtons = 5,
      ...props
    },
    ref
  ) => {
    const [uncontrolledCurrentPage, setUncontrolledCurrentPage] =
      React.useState(1);
    const [uncontrolledRowsPerPage, setUncontrolledRowsPerPage] =
      React.useState(rowsPerPageOptions[0] || 10);

    const currentPage =
      controlledCurrentPage !== undefined
        ? controlledCurrentPage
        : uncontrolledCurrentPage;

    const rowsPerPage =
      controlledRowsPerPage !== undefined
        ? controlledRowsPerPage
        : uncontrolledRowsPerPage;

    // Generate rows per page options
    const rowsPerPageSelectOptions: SelectOption[] = rowsPerPageOptions.map(
      (value) => {
        const valueStr = String(value);
        // Pad with leading zero if needed (manual implementation for compatibility)
        const label = valueStr.length < 2 ? "0" + valueStr : valueStr;
        return {
          id: `rows-${valueStr}`,
          value: valueStr,
          label: label,
          title: valueStr,
        };
      }
    );

    // Generate page options
    const pageOptions: SelectOption[] = Array.from(
      { length: totalPages },
      (_, i) => {
        const pageNum = i + 1;
        const pageStr = String(pageNum);
        return {
          id: `page-${pageStr}`,
          value: pageStr,
          label: pageStr,
          title: `Page ${pageNum}`,
        };
      }
    );

    const handlePageChange = (newPage: number) => {
      if (newPage < 1 || newPage > totalPages) return;
      if (controlledCurrentPage === undefined) {
        setUncontrolledCurrentPage(newPage);
      }
      onPageChange?.(newPage);
    };

    const handleRowsPerPageChange = (value: string | number) => {
      const newRowsPerPage =
        typeof value === "string" ? parseInt(value) : value;
      if (controlledRowsPerPage === undefined) {
        setUncontrolledRowsPerPage(newRowsPerPage);
      }
      onRowsPerPageChange?.(newRowsPerPage);
      // Reset to first page when rows per page changes
      handlePageChange(1);
    };

    const handlePrevPage = () => {
      handlePageChange(currentPage - 1);
    };

    const handleNextPage = () => {
      handlePageChange(currentPage + 1);
    };

    const isPrevDisabled = currentPage <= 1 || isDisabled;
    const isNextDisabled = currentPage >= totalPages || isDisabled;

    const selectSize =
      size === "small" ? "small" : size === "large" ? "large" : "medium";

    // Map pagination size to button size
    const buttonSize =
      size === "small" ? "xsmall" : size === "large" ? "large" : "small";

    // Generate page numbers to display
    const getPageNumbers = (): (number | string)[] => {
      if (totalPages <= maxPageButtons) {
        // Show all pages if total is less than max
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      // Determine page status if not provided
      const effectivePageStatus =
        pageStatus ||
        (currentPage <= 2
          ? "first"
          : currentPage >= totalPages - 1
          ? "last"
          : "middle");

      if (effectivePageStatus === "first") {
        // Show first pages: 1, 2, 3, 4, 5
        return Array.from(
          { length: Math.min(maxPageButtons, totalPages) },
          (_, i) => i + 1
        );
      } else if (effectivePageStatus === "last") {
        // Show last pages
        const start = totalPages - maxPageButtons + 1;
        return Array.from({ length: maxPageButtons }, (_, i) => start + i);
      } else if (effectivePageStatus === "middle") {
        // Show pages around current page
        const halfMax = Math.floor(maxPageButtons / 2);
        let start = currentPage - halfMax;
        let end = currentPage + halfMax;

        // Adjust if at boundaries
        if (start < 1) {
          end += 1 - start;
          start = 1;
        }
        if (end > totalPages) {
          start -= end - totalPages;
          end = totalPages;
        }

        start = Math.max(1, start);
        end = Math.min(totalPages, end);

        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
      }

      // pageStatus === "none" - don't show any page numbers
      return [];
    };

    const pageNumbers = getPageNumbers();

    return (
      <div
        ref={ref}
        className={cn(paginationVariants({ size }), className)}
        {...props}
      >
        {showRowsPerPage && (
          <div className="flex items-center gap-3">
            <span className="text-surface-ink-neutral-muted whitespace-nowrap">
              {rowsPerPageLabel}
            </span>
            <div className="w-[80px]">
              <Select
                value={rowsPerPage.toString()}
                options={rowsPerPageSelectOptions}
                onChange={handleRowsPerPageChange}
                size={selectSize}
                isDisabled={isDisabled}
                menuWidth="auto"
              />
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 ml-auto">
          {showPrevNext && (
            <Button
              variant="tertiary"
              color="neutral"
              size={buttonSize}
              onClick={handlePrevPage}
              isDisabled={isPrevDisabled}
              leadingIcon={<ChevronLeft className="w-4 h-4" />}
              aria-label="Previous page"
            >
              {prevLabel}
            </Button>
          )}

          {showPageJumper ? (
            // Show page dropdown selector
            <div className="flex items-center gap-3">
              <div className="w-[80px]">
                <Select
                  value={currentPage.toString()}
                  options={pageOptions}
                  onChange={(value) =>
                    handlePageChange(
                      typeof value === "string" ? parseInt(value) : value
                    )
                  }
                  size={selectSize}
                  isDisabled={isDisabled}
                  menuWidth="auto"
                />
              </div>
              <span className="text-surface-ink-neutral-muted whitespace-nowrap">
                {ofLabel} {totalPages}
              </span>
            </div>
          ) : (
            // Show numbered page buttons
            showPageNumber &&
            pageNumbers.length > 0 && (
              <ButtonGroup
                variant="separated"
                size={buttonSize}
                isDisabled={isDisabled}
                value={currentPage}
                onChange={(value) => handlePageChange(value as number)}
              >
                {pageNumbers.map((pageNum) => (
                  <Button
                    key={pageNum}
                    value={pageNum}
                    variant="tertiary"
                    color="primary"
                    aria-label={`Page ${pageNum}`}
                    aria-current={pageNum === currentPage ? "page" : undefined}
                  >
                    {pageNum}
                  </Button>
                ))}
              </ButtonGroup>
            )
          )}

          {showPrevNext && (
            <Button
              variant="tertiary"
              color="neutral"
              size={buttonSize}
              onClick={handleNextPage}
              isDisabled={isNextDisabled}
              trailingIcon={<ChevronRight className="w-4 h-4" />}
              aria-label="Next page"
            >
              {nextLabel}
            </Button>
          )}
        </div>
      </div>
    );
  }
);

Pagination.displayName = "Pagination";

export { paginationVariants };
